import React, { useState, useEffect } from "react";
import {
  makeStyles,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  IconButton,
  Select,
  Tooltip,
  InputAdornment,
} from "@material-ui/core";
import { Formik, Form, Field } from "formik";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ConfirmationModal from "../ConfirmationModal";

import { Edit as EditIcon } from "@material-ui/icons";
import SearchIcon from "@material-ui/icons/Search";

import { toast } from "react-toastify";
import useCompanies from "../../hooks/useCompanies";
import usePlans from "../../hooks/usePlans";
import { useDate } from "../../hooks/useDate";

import moment from "moment";
import { i18n } from "../../translate/i18n";
import { SectionCard, StatusBadge, PlanBadge } from "../Settings/AdminUI";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    padding: "2px"
  },
  mainPaper: {
    width: "100%",
    flex: 1,
    background: "transparent",
  },
  fullWidth: {
    width: "100%",
  },
  tableContainer: {
    width: "100%",
    overflowX: "auto",
    ...theme.scrollbarStyles,
    borderRadius: 14,
    border: "1px solid #edf0f5",
    boxShadow: "none",
  },
  textfield: {
    width: "100%",
  },
  textRight: {
    textAlign: "right",
  },
  row: {
    // paddingTop: theme.spacing(2),
    // paddingBottom: theme.spacing(2),
  },
  control: {
    // paddingRight: theme.spacing(1),
    // paddingLeft: theme.spacing(1),
  },
  buttonContainer: {
    textAlign: "right",
    // padding: theme.spacing(1),
  },
  modernInput: {
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      background: "#fafbfc",
      minHeight: 48,
      "& fieldset": {
        borderColor: "#e6eaf0",
      },
      "&:hover fieldset": {
        borderColor: "#cfd6e0",
      },
      "&.Mui-focused fieldset": {
        borderColor: theme.palette.primary.main,
        boxShadow: "0 0 0 3px rgba(63, 81, 181, 0.08)",
      },
    },
  },
  tableHead: {
    backgroundColor: "#f8fafc",
    "& .MuiTableCell-head": {
      color: "#475569",
      fontWeight: 800,
      whiteSpace: "nowrap",
      padding: theme.spacing(1.5),
    },
  },
  tableCell: {
    padding: theme.spacing(1.25, 1.5),
    whiteSpace: "nowrap",
    borderBottom: "1px solid #eef2f7",
  },
  filters: {
    display: "grid",
    gridTemplateColumns: "minmax(220px, 1fr) 180px 220px",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  formSection: {
    marginTop: theme.spacing(2.5),
    paddingTop: theme.spacing(2.5),
    borderTop: "1px solid #eef2f7",
    "&:first-of-type": {
      marginTop: 0,
      paddingTop: 0,
      borderTop: 0,
    },
  },
  sectionLabel: {
    marginBottom: theme.spacing(1),
    color: "#687182",
    fontSize: 12,
    fontWeight: 800,
    letterSpacing: 0.4,
    textTransform: "uppercase",
  },
  actionRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginTop: theme.spacing(2.5),
  },
}));

export function CompanyForm(props) {
  const { onSubmit, onDelete, onCancel, initialValue, loading } = props;
  const classes = useStyles();
  const [plans, setPlans] = useState([]);

  const [record, setRecord] = useState({
    name: "",
    email: "",
    phone: "",
    planId: "",
    status: true,
    // campaignsEnabled: false,
    dueDate: "",
    recurrence: "",
    password: "",
    generateInvoice: true,
    ...initialValue,
  });

  const { list: listPlans } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const list = await listPlans();
      setPlans(list);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setRecord((prev) => {
      if (moment(initialValue).isValid()) {
        initialValue.dueDate = moment(initialValue.dueDate).format(
          "YYYY-MM-DD"
        );
      }
      return {
        ...prev,
        ...initialValue,
      };
    });
  }, [initialValue]);

  const handleSubmit = async (data) => {
    if (data.dueDate === "" || moment(data.dueDate).isValid() === false) {
      data.dueDate = null;
    }
    console.log("Dados do formulário:", data);
    onSubmit(data);
    setRecord({ ...initialValue, dueDate: "", generateInvoice: true });
  };

  const incrementDueDate = () => {
    const data = { ...record };
    if (data.dueDate !== "" && data.dueDate !== null) {
      switch (data.recurrence) {
        case "MENSAL":
          data.dueDate = moment(data.dueDate)
            .add(1, "month")
            .format("YYYY-MM-DD");
          break;
        case "BIMESTRAL":
          data.dueDate = moment(data.dueDate)
            .add(2, "month")
            .format("YYYY-MM-DD");
          break;
        case "TRIMESTRAL":
          data.dueDate = moment(data.dueDate)
            .add(3, "month")
            .format("YYYY-MM-DD");
          break;
        case "SEMESTRAL":
          data.dueDate = moment(data.dueDate)
            .add(6, "month")
            .format("YYYY-MM-DD");
          break;
        case "ANUAL":
          data.dueDate = moment(data.dueDate)
            .add(12, "month")
            .format("YYYY-MM-DD");
          break;
        default:
          break;
      }
    }
    setRecord(data);
  };

  return (
    <>
      <Formik
        enableReinitialize
        className={classes.fullWidth}
        initialValues={record}
        onSubmit={(values, { resetForm }) =>
          setTimeout(() => {
            handleSubmit(values);
            resetForm();
          }, 500)
        }
      >
        {(values, setValues) => (
          <Form className={classes.fullWidth}>
            <SectionCard
              title={record.id ? "Editar empresa" : "Cadastro de empresa"}
              description="Acesso, assinatura e cobrança organizados em um fluxo mais claro."
            >
              <div className={classes.formSection}>
                <div className={classes.sectionLabel}>Identificação</div>
                <Grid spacing={2} container>
                  <Grid xs={12} sm={6} md={4} item>
                    <Field as={TextField} label={i18n.t("compaies.table.name")} name="name" variant="outlined" className={`${classes.fullWidth} ${classes.modernInput}`} margin="dense" />
                  </Grid>
                  <Grid xs={12} sm={6} md={4} item>
                    <Field as={TextField} label={i18n.t("compaies.table.email")} name="email" variant="outlined" className={`${classes.fullWidth} ${classes.modernInput}`} margin="dense" required />
                  </Grid>
                  <Grid xs={12} sm={6} md={4} item>
                    <Field as={TextField} label={i18n.t("compaies.table.password")} name="password" variant="outlined" className={`${classes.fullWidth} ${classes.modernInput}`} margin="dense" />
                  </Grid>
                  <Grid xs={12} sm={6} md={4} item>
                    <Field as={TextField} label={i18n.t("compaies.table.phone")} name="phone" variant="outlined" className={`${classes.fullWidth} ${classes.modernInput}`} margin="dense" />
                  </Grid>
                  <Grid xs={12} sm={6} md={4} item>
                    <Field as={TextField} label={i18n.t("compaies.table.document")} name="document" variant="outlined" className={`${classes.fullWidth} ${classes.modernInput}`} margin="dense" />
                  </Grid>
                  <Grid xs={12} sm={6} md={4} item>
                    <FormControl margin="dense" variant="outlined" fullWidth className={classes.modernInput}>
                      <InputLabel htmlFor="status-selection">{i18n.t("compaies.table.active")}</InputLabel>
                      <Field as={Select} id="status-selection" label={i18n.t("compaies.table.active")} labelId="status-selection-label" name="status" margin="dense">
                        <MenuItem value={true}>{i18n.t("compaies.table.yes")}</MenuItem>
                        <MenuItem value={false}>{i18n.t("compaies.table.no")}</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>
                </Grid>
              </div>

              <div className={classes.formSection}>
                <div className={classes.sectionLabel}>Plano e cobrança</div>
                <Grid spacing={2} container>
                  <Grid xs={12} sm={6} md={3} item>
                    <FormControl margin="dense" variant="outlined" fullWidth className={classes.modernInput}>
                      <InputLabel htmlFor="plan-selection">{i18n.t("compaies.table.plan")}</InputLabel>
                      <Field as={Select} id="plan-selection" label={i18n.t("compaies.table.plan")} labelId="plan-selection-label" name="planId" margin="dense" required>
                        {plans.map((plan, key) => (
                          <MenuItem key={key} value={plan.id}>
                            {plan.name}
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </Grid>
                  <Grid xs={12} sm={6} md={3} item>
                    <Field as={TextField} label={i18n.t("compaies.table.dueDate")} type="date" name="dueDate" InputLabelProps={{ shrink: true }} variant="outlined" fullWidth margin="dense" className={classes.modernInput} />
                  </Grid>
                  <Grid xs={12} sm={6} md={3} item>
                    <FormControl margin="dense" variant="outlined" fullWidth className={classes.modernInput}>
                      <InputLabel htmlFor="recorrencia-selection">{i18n.t("compaies.table.recurrence")}</InputLabel>
                      <Field as={Select} label="Recorrência" labelId="recorrencia-selection-label" id="recurrence" name="recurrence" margin="dense">
                        <MenuItem value="MENSAL">{i18n.t("compaies.table.monthly")}</MenuItem>
                        <MenuItem value="BIMESTRAL">{i18n.t("compaies.table.bimonthly")}</MenuItem>
                        <MenuItem value="TRIMESTRAL">{i18n.t("compaies.table.quarterly")}</MenuItem>
                        <MenuItem value="SEMESTRAL">{i18n.t("compaies.table.semester")}</MenuItem>
                        <MenuItem value="ANUAL">{i18n.t("compaies.table.yearly")}</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>
                  <Grid xs={12} sm={6} md={3} item>
                    <FormControl margin="dense" variant="outlined" fullWidth className={classes.modernInput}>
                      <InputLabel htmlFor="generate-invoice-selection">Gerar Fatura</InputLabel>
                      <Field as={Select} id="generate-invoice-selection" label="Gerar Fatura" labelId="generate-invoice-selection-label" name="generateInvoice" margin="dense">
                        <MenuItem value={true}>Sim</MenuItem>
                        <MenuItem value={false}>Não</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>
                </Grid>
              </div>

              <div className={classes.actionRow}>
                <ButtonWithSpinner loading={loading} onClick={() => onCancel()} variant="contained">
                  {i18n.t("compaies.table.clear")}
                </ButtonWithSpinner>
                {record.id !== undefined ? (
                  <>
                    <ButtonWithSpinner loading={loading} onClick={() => onDelete(record)} variant="contained" color="secondary" disabled={record.id === 1}>
                      {i18n.t("compaies.table.delete")}
                    </ButtonWithSpinner>
                    <ButtonWithSpinner loading={loading} onClick={() => incrementDueDate()} variant="contained" color="primary">
                      {i18n.t("compaies.table.dueDate")}
                    </ButtonWithSpinner>
                  </>
                ) : null}
                <ButtonWithSpinner loading={loading} type="submit" variant="contained" color="primary">
                  {i18n.t("compaies.table.save")}
                </ButtonWithSpinner>
              </div>
            </SectionCard>
          </Form>
        )}
      </Formik>
    </>
  );
}

export function CompaniesManagerGrid(props) {
  const { records, onSelect } = props;
  const classes = useStyles();
  const { dateToClient, datetimeToClient } = useDate();
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [planFilter, setPlanFilter] = useState("all");

  const renderPlan = (row) => {
    return row.planId !== null ? row.plan.name : "-";
  };

  const renderPlanValue = (row) => {
    return row.planId !== null ? row.plan.amount ? row.plan.amount.toLocaleString('pt-br', { minimumFractionDigits: 2 }) : '00.00' : "-";
  };

  const planOptions = Array.from(
    new Map(
      records
        .filter((record) => record.plan)
        .map((record) => [record.planId, record.plan])
    ).values()
  );

  const filteredRecords = records.filter((record) => {
    const query = search.trim().toLowerCase();
    const matchesSearch =
      !query ||
      (record.name || "").toLowerCase().includes(query) ||
      (record.email || "").toLowerCase().includes(query);
    const matchesStatus =
      statusFilter === "all" ||
      String(record.status === false ? false : true) === statusFilter;
    const matchesPlan =
      planFilter === "all" || String(record.planId || "") === planFilter;

    return matchesSearch && matchesStatus && matchesPlan;
  });

  // const renderCampaignsStatus = (row) => {
  //   if (
  //     has(row, "settings") &&
  //     isArray(row.settings) &&
  //     row.settings.length > 0
  //   ) {
  //     const setting = row.settings.find((s) => s.key === "campaignsEnabled");
  //     if (setting) {
  //       return setting.value === "true" ? "Habilitadas" : "Desabilitadas";
  //     }
  //   }
  //   return "Desabilitadas";
  // };

  const rowStyle = (record) => {
    if (moment(record.dueDate).isValid()) {
      const now = moment();
      const dueDate = moment(record.dueDate);
      const diff = dueDate.diff(now, "days");
      if (diff >= 1 && diff <= 5) {
        return { backgroundColor: "#fffead" };
      }
      if (diff <= 0) {
        return { backgroundColor: "#fa8c8c" };
      }
    }
    return {};
  };
  
  const cellStyle = (record) => {
    if (moment(record.dueDate).isValid()) {
      const now = moment();
      const dueDate = moment(record.dueDate);
      const diff = dueDate.diff(now, "days");
      if (diff >= 1 && diff <= 5) {
        return { color: "#000" }; // Texto preto para fundo amarelo
      }
      if (diff <= 0) {
        return { color: "#fff" }; // Texto branco para fundo vermelho
      }
    }
    return {};
  };

  const iconStyle = (record) => {
    if (moment(record.dueDate).isValid()) {
      const now = moment();
      const dueDate = moment(record.dueDate);
      const diff = dueDate.diff(now, "days");
      if (diff >= 1 && diff <= 5) {
        return { color: "#000" }; // Ícone preto para fundo amarelo
      }
    }
    return {};
  };

  return (
    <SectionCard
      title="Empresas cadastradas"
      description="Busque, filtre e edite empresas sem sair da administração."
    >
      <div className={classes.filters}>
        <TextField
          variant="outlined"
          size="small"
          label="Buscar por nome ou email"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          className={classes.modernInput}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <FormControl variant="outlined" size="small" className={classes.modernInput}>
          <InputLabel>Status</InputLabel>
          <Select
            label="Status"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <MenuItem value="all">Todos</MenuItem>
            <MenuItem value="true">Ativas</MenuItem>
            <MenuItem value="false">Inativas</MenuItem>
          </Select>
        </FormControl>
        <FormControl variant="outlined" size="small" className={classes.modernInput}>
          <InputLabel>Plano</InputLabel>
          <Select
            label="Plano"
            value={planFilter}
            onChange={(event) => setPlanFilter(event.target.value)}
          >
            <MenuItem value="all">Todos</MenuItem>
            {planOptions.map((plan) => (
              <MenuItem key={plan.id} value={String(plan.id)}>
                {plan.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
    <Paper className={classes.tableContainer} elevation={0}>
      <Table
        className={classes.fullWidth}
        // size="small"
        padding="none"
        aria-label="a dense table"
      >
        <TableHead className={classes.tableHead}>
          <TableRow>
            <TableCell align="center" style={{ width: "1%" }}>#</TableCell>
            <TableCell align="left">{i18n.t("compaies.table.name")}</TableCell>
            <TableCell align="left">{i18n.t("compaies.table.email")}</TableCell>
            <TableCell align="center">{i18n.t("compaies.table.phone")}</TableCell>
            <TableCell align="center">{i18n.t("compaies.table.plan")}</TableCell>
            <TableCell align="center">{i18n.t("compaies.table.value")}</TableCell>
            {/* <TableCell align="center">Campanhas</TableCell> */}
            <TableCell align="center">{i18n.t("compaies.table.active")}</TableCell>
            <TableCell align="center">{i18n.t("compaies.table.createdAt")}</TableCell>
            <TableCell align="center">{i18n.t("compaies.table.dueDate")}</TableCell>
            <TableCell align="center">{i18n.t("compaies.table.lastLogin")}</TableCell>
            <TableCell align="center">{i18n.t("compaies.table.generateInvoice")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredRecords.map((row, key) => (
            <TableRow style={rowStyle(row)} key={key}>
              <TableCell className={classes.tableCell} style={{...cellStyle(row), width: "1%"}} align="center">
                <Tooltip title="Editar empresa">
                  <IconButton size="small" style={iconStyle(row)} onClick={() => onSelect(row)} aria-label="editar">
                    <EditIcon fontSize="small" style={iconStyle(row)} />
                  </IconButton>
                </Tooltip>
              </TableCell>
              <TableCell className={classes.tableCell} style={cellStyle(row)} align="left">{row.name || "-"}</TableCell>
              <TableCell className={classes.tableCell} style={cellStyle(row)} align="left" size="small">{row.email || "-"}</TableCell>
              <TableCell className={classes.tableCell} style={cellStyle(row)} align="center">{row.phone || "-"}</TableCell>
              <TableCell className={classes.tableCell} style={cellStyle(row)} align="center"><PlanBadge>{renderPlan(row)}</PlanBadge></TableCell>
              <TableCell className={classes.tableCell} style={cellStyle(row)} align="center">{i18n.t("compaies.table.money")} {renderPlanValue(row)}</TableCell>
              {/* <TableCell style={cellStyle(row)} align="center">{renderCampaignsStatus(row)}</TableCell> */}
              <TableCell className={classes.tableCell} style={cellStyle(row)} align="center"><StatusBadge active={row.status !== false} trueLabel="Ativa" falseLabel="Inativa" /></TableCell>
              <TableCell className={classes.tableCell} style={cellStyle(row)} align="center">{dateToClient(row.createdAt)}</TableCell>
              <TableCell className={classes.tableCell} style={cellStyle(row)} align="center">{dateToClient(row.dueDate)}<br /><span>{row.recurrence}</span></TableCell>
              <TableCell className={classes.tableCell} style={cellStyle(row)} align="center">{datetimeToClient(row.lastLogin)}</TableCell>
              <TableCell className={classes.tableCell} style={cellStyle(row)} align="center"><StatusBadge active={row.generateInvoice} trueLabel="Sim" falseLabel="Não" /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
    </SectionCard>
  );
}

export default function CompaniesManager() {
  const classes = useStyles();
  const { list, save, update, remove } = useCompanies();

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [record, setRecord] = useState({
    name: "",
    email: "",
    phone: "",
    planId: "",
    status: true,
    // campaignsEnabled: false,
    dueDate: "",
    recurrence: "",
    password: "",
    document: "",
    paymentMethod: "",
    generateInvoice: true
  });

  useEffect(() => {
    loadPlans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadPlans = async () => {
    setLoading(true);
    try {
      const companyList = await list();
      console.log("Lista de empresas carregada:", companyList);
      setRecords(companyList);
    } catch (e) {
      toast.error("Não foi possível carregar a lista de registros");
    }
    setLoading(false);
  };

  const handleSubmit = async (data) => {
    console.log("Dados enviados para o backend:", data);
    setLoading(true);
    try {
      if (data.id !== undefined) {
        await update(data);
      } else {
        await save(data);
      }
      await loadPlans();
      handleCancel();
      toast.success("Operação realizada com sucesso!");
    } catch (e) {
      toast.error(
        "Não foi possível realizar a operação. Verifique se já existe uma empresa com o mesmo nome ou se os campos foram preenchidos corretamente"
      );
    }
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await remove(record.id);
      await loadPlans();
      handleCancel();
      toast.success("Operação realizada com sucesso!");
    } catch (e) {
      toast.error("Não foi possível realizar a operação");
    }
    setLoading(false);
  };

  const handleOpenDeleteDialog = () => {
    setShowConfirmDialog(true);
  };

  const handleCancel = () => {
    setRecord((prev) => ({
      ...prev,
      name: "",
      email: "",
      phone: "",
      planId: "",
      status: true,
      // campaignsEnabled: false,
      dueDate: "",
      recurrence: "",
      password: "",
      document: "",
      paymentMethod: "",
      generateInvoice: true
    }));
  };

  const handleSelect = (data) => {
    // let campaignsEnabled = false;

    // const setting = data.settings.find(
    //   (s) => s.key.indexOf("campaignsEnabled") > -1
    // );
    // if (setting) {
    //   campaignsEnabled = setting.value === "true" || setting.value === "enabled";
    // }

    console.log("Dados da empresa selecionada:", data);
    setRecord((prev) => ({
      ...prev,
      id: data.id,
      name: data.name || "",
      phone: data.phone || "",
      email: data.email || "",
      planId: data.planId || "",
      status: data.status === false ? false : true,
      // campaignsEnabled,
      dueDate: data.dueDate || "",
      recurrence: data.recurrence || "",
      password: "",
      document: data.document || "",
      paymentMethod: data.paymentMethod || "",
      generateInvoice: data.generateInvoice !== undefined ? data.generateInvoice : true,
    }));
  };

  return (
    <Paper className={classes.mainPaper} elevation={0}>
      <Grid spacing={2} container>
        <Grid xs={12} item>
          <CompanyForm
            initialValue={record}
            onDelete={handleOpenDeleteDialog}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            loading={loading}
          />
        </Grid>
        <Grid xs={12} item>
          <CompaniesManagerGrid records={records} onSelect={handleSelect} />
        </Grid>
      </Grid>
      <ConfirmationModal
        title="Exclusão de Registro"
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={() => handleDelete()}
      >
        Deseja realmente excluir esse registro?
      </ConfirmationModal>
    </Paper>
  );
}
